import { getPrefix } from "features/Application/slices/models";
import type { MessageIdsType, UpdateParams } from "interfaces";
import accountsDataService from "features/Accounts/services/accountsDataService";
import backgroundTask from "features/BackgroundTasks/backgroundTask";
import { ReducerEntityPrefixTypes, ReducerNamespaceTypes } from "enums/reducer";
import { type AddOn, type SavingAddOn } from "features/Accounts/types";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { type AppDispatch } from "features/Application/globaltypes/redux";
import { type AxiosResponse } from "axios";
import { fetchPermissionsToAccount } from "features/Accounts/EditAccount/state/thunks/permissionsToAccountThunk";

const updateAddOnsPrefixPayload = {
  namespace: ReducerNamespaceTypes.Accounts,
  entity: ReducerEntityPrefixTypes.AccountCreation,
  name: "updateAddOns",
};

const addOnsPrefixPayload = {
  namespace: ReducerNamespaceTypes.Accounts,
  entity: ReducerEntityPrefixTypes.AccountCreation,
  name: "addOns",
};

export const updateAddOns = createAsyncThunk(
  getPrefix(updateAddOnsPrefixPayload),
  async ({ accountId, addOns }: { accountId: number; addOns: SavingAddOn[] }, { dispatch }) => {
    const params: UpdateParams = {
      id: "UpdateAccountAddOns",
      title: "Update account Add-Ons",
      indeterminate: true,
      getMessageIds: async (): Promise<MessageIdsType> => {
        const messageId = await accountsDataService.updateAddOns(accountId, addOns);
        return [messageId];
      },
      successTransientMessage: "Account Add-Ons update succeeded!",
      failureTransientMessage: "Account Add-Ons update failed!",
      onCompleted: (_: string) => dispatch(fetchPermissionsToAccount(accountId)),
    };
    await backgroundTask.initializeBackgroundTask(params, dispatch as AppDispatch);
  },
);

export const assignAddOns = createAsyncThunk(
  getPrefix(updateAddOnsPrefixPayload),
  async ({ accountId, addOns }: { accountId: number; addOns: SavingAddOn[] }, { dispatch }) => {
    const params = {
      id: "UpdateAccountAddOns",
      title: "Assign account Add-Ons",
      indeterminate: true,
      getMessageIds: async (): Promise<MessageIdsType> => {
        const messageId = await accountsDataService.updateAddOns(accountId, addOns);
        return [messageId];
      },
      successTransientMessage: "Account Add-Ons have been assigned successfully!",
      failureTransientMessage: "Account Add-Ons have not been assigned!",
    };
    await backgroundTask.initializeBackgroundTask(params, dispatch as AppDispatch);
  },
);

const sortOrder = [
  "Manage Content",
  "Marketplace",
  "Purchase Packs",
  "Manage Accounts",
  "Manage Templates",
  "Threat Defense",
];

export const fetchAddOns = createAsyncThunk<AddOn[], number, { rejectValue: AxiosResponse }>(
  getPrefix(addOnsPrefixPayload),
  async (accountId: number, { rejectWithValue }) => {
    try {
      const addOns = await accountsDataService.getAddOns(accountId);

      // Sort addOns by specified order
      return addOns.sort((a, b) => {
        const aIndex = sortOrder.indexOf(a.name);
        const bIndex = sortOrder.indexOf(b.name);
        // If not found, put at the end
        return (aIndex === -1 ? sortOrder.length : aIndex) - (bIndex === -1 ? sortOrder.length : bIndex);
      });
    } catch (error) {
      return rejectWithValue(error.response);
    }
  },
);
